import type { Card as CardType, NumberCardValue, ActionCardValue, ShinyTier } from '@flip7/shared';
import { NUMBER_CARD_COLORS, NUMBER_CARD_NAMES } from '@flip7/shared';

interface CardProps {
  card: CardType;
  selected?: boolean;
  onClick?: () => void;
  /** Override shiny tier. When omitted, uses card.shinyTier from server. */
  shinyTier?: ShinyTier;
}

export function Card({ card, selected, onClick, shinyTier: shinyTierProp }: CardProps) {
  const shinyTier = shinyTierProp ?? card.shinyTier;

  const className = [
    'card',
    `card--${card.type}`,
    card.type === 'action' ? `card--${card.value}` : '',
    selected ? 'card--selected' : '',
    shinyTier ? `card--shiny card--shiny-${shinyTier}` : '',
  ].filter(Boolean).join(' ');

  const shinyOverlay = shinyTier ? <><span className="card__shiny-overlay" /><span className="card__shiny-sweep" /><span className="card__shiny-sparkle" /></> : null;
  const Tag = onClick ? 'button' : 'div';

  if (card.type === 'number') {
    const color = NUMBER_CARD_COLORS[card.value as NumberCardValue];
    const baseName = NUMBER_CARD_NAMES[card.value as NumberCardValue];
    const special = 'special' in card ? card.special : undefined;
    const displayName = special === 'unlucky7' ? 'UNLUCKY 7'
      : special === 'lucky13' ? 'LUCKY 13'
      : baseName;
    const specialIcon = special === 'unlucky7' ? '\uD83D\uDC80 '
      : special === 'lucky13' ? '\uD83C\uDF40 '
      : '';
    return (
      <Tag
        className={`${className}${special ? ' card--special' : ''}`}
        onClick={onClick}
        style={{
          '--chip-color': special === 'unlucky7' ? '#8B0000' : special === 'lucky13' ? '#2E7D32' : color,
        } as React.CSSProperties}
        aria-label={`${displayName} card, value ${card.value}`}
        aria-pressed={selected}
      >
        {shinyOverlay}
        <span className="card__number" style={{ color: special === 'unlucky7' ? '#8B0000' : special === 'lucky13' ? '#2E7D32' : color }}>
          {specialIcon}{card.value}
        </span>
        <span className="card__name">{displayName}</span>
        <style>{`
          .card--number[style*="--chip-color: ${color}"]::before,
          .card--number[style*="--chip-color: ${color}"]::after {
            background: ${color};
          }
        `}</style>
      </Tag>
    );
  }

  if (card.type === 'modifier') {
    return (
      <Tag
        className={className}
        onClick={onClick}
        aria-label={`Modifier ${card.value}`}
        aria-pressed={selected}
      >
        {shinyOverlay}
        <span className="card__modifier-value">{card.value === 'div2' ? '÷2' : card.value}</span>
        <span className="card__modifier-label">modifier</span>
      </Tag>
    );
  }

  // Action card
  const icons: Record<ActionCardValue, string> = {
    'flip-three': '\u26A1',
    'freeze': '\uD83D\uDD12',
    'second-chance': '\u2764\uFE0F',
    'just-one-more': '\u270B',
    'swap': '\uD83D\uDD00',
    'steal': '\uD83E\uDD1E',
    'discard': '\uD83D\uDDD1\uFE0F',
    'flip-four': '\u26A1\u26A1',
  };
  const names: Record<ActionCardValue, string> = {
    'flip-three': 'FLIP THREE',
    'freeze': 'FREEZE',
    'second-chance': 'SECOND CHANCE',
    'just-one-more': 'JUST ONE MORE',
    'swap': 'SWAP',
    'steal': 'STEAL',
    'discard': 'DISCARD',
    'flip-four': 'FLIP FOUR',
  };

  return (
    <Tag
      className={className}
      onClick={onClick}
      aria-label={`${names[card.value as ActionCardValue]} card`}
      aria-pressed={selected}
    >
      {shinyOverlay}
      <span className="card__action-icon">{icons[card.value as ActionCardValue]}</span>
      <span className="card__action-name">{names[card.value as ActionCardValue]}</span>
    </Tag>
  );
}
